import { createContext, useContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import type { PermissionsScope } from '../core';
import type { CommunityId } from 'domain/CommunityId';

/** Exactly one target, the same discipline the port requires of an adapter. */
const toScope = (
  communityId: CommunityId | undefined,
  organisationId: number | undefined
): PermissionsScope | undefined => {
  if (communityId !== undefined) return { communityId };
  return organisationId !== undefined ? { organisationId } : undefined;
};

/**
 * Which community or organisation the questions in this subtree are about.
 *
 * `undefined` - no provider anywhere above - means the active community, which is what every gate in
 * the app implicitly asks about today. So this layer needs no wiring at the app root, and a
 * component under test is gated correctly without one.
 */
const PermissionsScopeContext = createContext<PermissionsScope | undefined>(undefined);

/**
 * Internal to this layer: `usePermissionsSnapshot` is the only intended reader, which is why it is
 * absent from the barrel. A call site that needs a different scope wraps a provider around itself
 * rather than passing a scope down.
 */
export const usePermissionsScope = () => useContext(PermissionsScopeContext);

/**
 * Scopes every `useCan` beneath it.
 *
 * One target per scope, matching the endpoint's contract (ADR-0007), so a permission held in one
 * community cannot answer for another. Nesting narrows: the organisation console wraps its own
 * subtree without disturbing the rest of the app, which keeps asking about the active community.
 */
export const PermissionsProvider = ({ communityId, organisationId, children }: PropsWithChildren<PermissionsScope>) => {
  // Keyed on the ids rather than on an object built in the caller's render, so a subtree only
  // re-renders when the target actually changes.
  const scope = useMemo(() => toScope(communityId, organisationId), [communityId, organisationId]);

  return <PermissionsScopeContext.Provider value={scope}>{children}</PermissionsScopeContext.Provider>;
};
